import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  inject
} from '@angular/core';
import { Router } from '@angular/router';
import { ChartData } from 'chart.js';
import { CompanyVendor } from 'src/app/classes/models';
import { PercentageChart } from 'src/app/interfaces/percentage-chart';
import { WidgetPanelChart } from 'src/app/interfaces/widget-panel-chart';
import { CompanyService } from 'src/app/services/company.service';
import { SharedDataService } from 'src/app/services/shared-data.service';

type VendorStatus = {
  key: string;
  value: number;
};

@Component({
  selector: 'app-vendor-statuses-widget',
  templateUrl: './vendor-statuses-widget.component.html',
  styleUrls: ['./vendor-statuses-widget.component.less'],
  providers: [SharedDataService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class VendorStatusesWidgetComponent implements OnInit {
  private _cdr = inject(ChangeDetectorRef);

  constructor(
    private CompanyService: CompanyService,
    private Router: Router,
    private SharedDataService: SharedDataService
  ) {
    this.SharedDataService.listenForEvent<
      string | null | undefined
    >().subscribe({
      next: ({ eventName, data }) => {
        if (
          eventName === 'viveClientStatusLabelSelected' &&
          typeof data === 'string'
        ) {
          this.navigateToMyVendorGrid(data);
        }
        this._cdr.markForCheck();
      }
    });
  }

  @Input() CompanyId!: number;

  title: string = 'Vendor Status';
  percentageCharts: PercentageChart[] = [];

  widgetPanelCharts: WidgetPanelChart[] = [];

  // widgetPanel = { style: { height: '100%' } };
  widgetPanel = { style: {} };

  isScreenSizeSmall: boolean =
    (window.innerWidth ||
      document.documentElement.clientWidth ||
      document.body.clientWidth) < 1350;

  private formatData(data: VendorStatus[]) {
    const chartData: ChartData = {
      datasets: [
        {
          data: data.map((item) => item.value)
        }
      ],
      labels: data.map((item) => item.key)
    };

    this.widgetPanelCharts.push({
      title: this.title,
      // noDataMessage: ''
      data: {
        chartType: 'doughnut',
        chartTitle: this.title,
        chartData: chartData,
        chartOptions: {
          maintainAspectRatio: false,
          layout: {
            padding: this.isScreenSizeSmall ? 0 : 20
          },
          aspectRatio: 1,
          cutout: this.isScreenSizeSmall ? '75%' : '80%',
          plugins: {
            legend: {
              display: true,
              position: 'right',
              labels: {
                usePointStyle: true
              }
            },
            tooltip: {
              enabled: true
            }
          },
          events: ['mousemove', 'mouseout', 'touchstart', 'touchmove', 'click']
        },
        chartPlugins: [
          {
            id: 'centerText',
            afterDatasetDraw(chart, args, pluginOptions) {
              const { ctx, data } = chart;
              const totalVendorStatuses = String(
                data.datasets[0].data.reduce((acc, current) => {
                  if (!acc) {
                    acc = 0;
                  }
                  if (Array.isArray(current)) {
                    current = current[0];
                  }

                  return Number(acc) + Number(current);
                }, 0)
              );

              const isScreenSizeSmall: boolean =
                (window.innerWidth ||
                  document.documentElement.clientWidth ||
                  document.body.clientWidth) < 1350;

              ctx.save();
              const x = chart.getDatasetMeta(0).data[0].x;
              const y = chart.getDatasetMeta(0).data[0].y;
              ctx.font = isScreenSizeSmall ? '26px Rubik' : '32px Rubik';
              ctx.fillStyle = 'rgb(79, 92, 101)';
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              ctx.fillText(totalVendorStatuses, x, y - 10);

              ctx.font = isScreenSizeSmall ? '14px Rubik' : '18px Rubik';
              ctx.fillText('Vendors', x, y + 15);
            }
          },
          {
            id: 'percentages',
            afterDatasetDraw(chart, args, pluginOptions) {
              const {
                ctx,
                data,
                chartArea: { width, height }
              } = chart;
              const totalVendorStatuses = data.datasets[0].data.reduce(
                (acc, current) => {
                  if (!acc) {
                    acc = 0;
                  }
                  if (Array.isArray(current)) {
                    current = current[0];
                  }

                  return Number(acc) + Number(current);
                },
                0
              );

              let isScreenSizeSmall: boolean =
                (window.innerWidth ||
                  document.documentElement.clientWidth ||
                  document.body.clientWidth) < 1350;
              isScreenSizeSmall = false;

              ctx.save();
              const halfWidth = width / 2;
              const halfHeight = height / 2;

              data.datasets[0].data.forEach((item, index) => {
                let { x, y } = chart
                  .getDatasetMeta(0)
                  .data[index].tooltipPosition(true);

                if (!x || !y) {
                  return;
                }

                const percentage = Math.round(
                  (Number(item) / Number(totalVendorStatuses)) * 100
                ).toString();

                const distanceX = x - halfWidth;
                const distanceY = -(y - halfHeight);

                const radius = Math.sqrt(
                  Math.pow(distanceX, 2) + Math.pow(distanceY, 2)
                );
                const angle = Math.acos(distanceX / radius);

                const updatedX = halfWidth + (radius + 25) * Math.cos(angle);
                const updatedY =
                  y > halfHeight
                    ? halfHeight + (radius + 25) * Math.sin(angle)
                    : halfHeight - (radius + 25) * Math.sin(angle);

                ctx.font = '12px Rubik';
                ctx.fillStyle = isScreenSizeSmall
                  ? 'rgb(255, 255, 255)'
                  : 'rgb(79, 92, 101)';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(
                  `${percentage}%`,
                  isScreenSizeSmall ? x : updatedX,
                  isScreenSizeSmall ? y : updatedY
                );
              });
            }
          }
        ],
        chartStyle: {
          // padding: '1em 0',
        },
        displayChartTitle: false
      }
    });
  }

  private navigateToMyVendorGrid(
    viveStatus: CompanyVendor['viveStatus'] | string
  ) {
    const ViveStatusValues = [viveStatus];

    if (viveStatus === 'Registering') {
      ViveStatusValues.push('Awaiting Payment');
    }

    if (viveStatus === 'Reviewing') {
      ViveStatusValues.push('Registered', 'Renewing');
    }

    this.Router.navigateByUrl(`client/${this.CompanyId}/vendors/client`, {
      state: {
        title: viveStatus,
        FilterModel: {
          viveStatus: {
            type: 'set',
            values: ViveStatusValues
          }
        }
      }
    });
  }

  private loadCompanyVendorStatuses(CompanyId: number) {
    this.CompanyService.getCompanyVendorStatuses(CompanyId).subscribe({
      next: (vendorStatuses: VendorStatus[]) => {
        // We are grouping some statuses together to save space in chart
        const groupStatuses: { [key: string]: number } = vendorStatuses.reduce(
          (acc: { [key: string]: number }, current) => {
            let key = current.key;

            if (['Awaiting Payment'].includes(current.key)) {
              key = 'Registering';
            }

            if (['Registered', 'Renewing'].includes(current.key)) {
              key = 'Reviewing';
            }

            if (!acc[key]) {
              acc[key] = 0;
            }

            acc[key] += current.value;

            return acc;
          },
          {}
        );

        const formattedVendorStatuses = Object.entries(groupStatuses)
          .map(([key, value]) => ({ key, value }))
          .filter((status) => status.key !== 'Compliance Exempt');

        if (formattedVendorStatuses && formattedVendorStatuses.length > 0) {
          this.formatData(formattedVendorStatuses);
        }
        this._cdr.markForCheck();
      }
    });
  }

  ngOnInit(): void {
    this.loadCompanyVendorStatuses(this.CompanyId);
  }
}
